import { useEffect, useRef, useState } from "react";
import { useChatStream } from "@/hooks/useChatStream";
import { useAuth } from "@/auth/useAuth";
import { api, performLogout } from "@/api/client";
import CustomerSelect from "@/components/CustomerSelect";
import MessageList from "@/components/MessageList";
import Composer from "@/components/Composer";
import Toasts from "@/components/Toasts";
import HistoryDrawer from "@/components/HistoryDrawer";
import BookmarksDrawer from "@/components/BookmarksDrawer";
import ShareDialog from "@/components/ShareDialog";
import HomeWidgets from "@/components/HomeWidgets";
import AnnouncementBanner from "@/components/AnnouncementBanner";
import SettingsPanel from "@/components/SettingsPanel";
import SessionGuard from "@/components/SessionGuard";
import CustomerChangeDialog from "@/components/CustomerChangeDialog";
import SchedulePanel from "@/components/schedule/SchedulePanel";
import DictationPanel from "@/components/DictationPanel";
import UpcomingSchedules from "@/components/schedule/UpcomingSchedules";
import { useUpcomingSchedules } from "@/hooks/useUpcomingSchedules";
import { toast } from "@/lib/toast";
import { getSettings, setSettings } from "@/lib/settings";
import { CalendarDays, Loader2, Menu, LogOut, Mic, Plus, Download, Settings as SettingsIcon, Star, Share2 } from "lucide-react";
import type { ChatModelOption, ScheduleItem } from "@/api/types";

const LAST_CUSTOMER_KEY = "arkiveai.chat.customer";

export default function Chat() {
  const { user } = useAuth();
  const {
    messages,
    busy,
    threadId,
    send,
    runSkill,
    stop,
    newThread,
    loadThread,
    confirmExternal,
    setFeedback,
    setBookmark,
    regenerateWithSources,
  } = useChatStream();
  const [customer, setCustomer] = useState<string>(
    () => localStorage.getItem(LAST_CUSTOMER_KEY) ?? "",
  );
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [bookmarksOpen, setBookmarksOpen] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [scheduleOpen, setScheduleOpen] = useState(false); // v3.26 일정 관리 패널
  const [dictationOpen, setDictationOpen] = useState(false); // 받아쓰기(음성→텍스트) 패널
  // 홈 '다가오는 일정'에서 특정 일정을 눌러 열면 그 상세까지 펼친다.
  const [scheduleFocus, setScheduleFocus] = useState<ScheduleItem | null>(null);
  const [seed, setSeed] = useState<{ text: string; nonce: number } | undefined>(undefined);
  // v3.5: 분야 변경 3버튼 다이얼로그 — 변경 대상 분야를 담아두고 사용자의 선택을 기다린다
  const [pendingCustomer, setPendingCustomer] = useState<string | null>(null);

  // 다가오는 일정 — 홈 위젯(목록)과 헤더 배지(오늘 건수)가 같은 조회를 공유한다.
  const upcoming = useUpcomingSchedules();

  const openSchedule = (item?: ScheduleItem) => {
    setScheduleFocus(item ?? null);
    setScheduleOpen(true);
  };

  // ── 답변 모델 셀렉터(관리자 등록) ──
  // 정책: 사용자가 고른 모델을 계속 유지한다(전송 후에도 복귀 없음, 새로고침에도 보존).
  // 저장된 선택이 없거나 그 모델이 사라졌으면 기본값 모델로 시작한다.
  const [models, setModels] = useState<ChatModelOption[]>([]);
  const [modelId, setModelId] = useState<number | null>(null);
  useEffect(() => {
    api
      .models()
      .then((r) => {
        const items = r.items ?? [];
        setModels(items);
        const fallback = items.find((m) => m.is_default)?.id ?? items[0]?.id ?? null;
        const saved = getSettings().chatModelId;
        // 저장값이 현재 목록(활성 모델)에 남아 있을 때만 복원 — 삭제/비활성이면 기본값
        setModelId(saved != null && items.some((m) => m.id === saved) ? saved : fallback);
      })
      .catch(() => {
        /* 모델 미등록/조회 실패 — 셀렉터 숨김(서버 기본 동작) */
      });
  }, []);
  // 셀렉터 변경 — 즉시 localStorage 에 기억시켜 다음 방문에도 같은 모델을 쓴다
  const changeModel = (id: number) => {
    setModelId(id);
    setSettings({ chatModelId: Number.isFinite(id) ? id : null });
  };

  // 새로고침 시 마지막 대화 자동 복원 (서버 이력 기준, 1회)
  // ⚠ "첫 화면이 떴다가 이전 대화로 화면이 넘어가는" 점프를 두 갈래로 막는다:
  //  ① restoring 동안 홈(첫 화면)을 그리지 않는다 — 복원 결과가 온 뒤 홈/대화를 결정.
  //  ② 복원 응답이 오기 전에 사용자가 이미 질문·새 대화를 시작했으면(interactedRef)
  //     늦게 도착한 복원 결과를 버린다 — 진행 중인 검색/대화를 이전 대화로 덮어쓰지 않도록.
  //     (기존: 복원 fetch 가 느릴 때 사용자가 먼저 검색하면 loadThread 가 스트리밍을
  //      중단시키고 화면을 이전 대화로 교체하던 경합)
  const restoredRef = useRef(false);
  const interactedRef = useRef(false);
  const [restoring, setRestoring] = useState(true);
  useEffect(() => {
    if (restoredRef.current) return;
    restoredRef.current = true;
    (async () => {
      try {
        const d = await api.historyDetail(threadId);
        if (d.turns?.length && !interactedRef.current) loadThread(threadId, d.turns);
      } catch {
        /* 이력 없음/오류 — 빈 화면 유지 */
      } finally {
        setRestoring(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const applyCustomer = (v: string) => {
    setCustomer(v);
    localStorage.setItem(LAST_CUSTOMER_KEY, v);
  };

  const onCustomer = (v: string) => {
    if (v === customer) return;
    // 진행 중인 대화가 있으면 3버튼 다이얼로그로 확인
    // (변경하고 새 대화 / 변경하고 기존 대화 유지 / 취소)
    if (messages.length > 0) {
      setPendingCustomer(v);
      return;
    }
    applyCustomer(v);
  };

  // 다이얼로그 선택 핸들러 — 새 대화로 변경 / 기존 대화 유지한 채 변경
  const changeWithNewChat = () => {
    if (pendingCustomer === null) return;
    interactedRef.current = true;
    newThread();
    applyCustomer(pendingCustomer);
    setPendingCustomer(null);
  };
  const changeKeepChat = () => {
    if (pendingCustomer === null) return;
    applyCustomer(pendingCustomer);
    setPendingCustomer(null);
  };

  // pilgrims 공유 쿠키까지 만료시키는 완전 로그아웃(로컬 세션만 지우면 자동 재로그인됨).
  const onLogout = () => performLogout();

  const handleSelect = async (tid: string) => {
    interactedRef.current = true; // 사용자가 명시 선택 — 늦은 자동복원이 덮지 않도록
    try {
      const d = await api.historyDetail(tid);
      loadThread(tid, d.turns);
    } catch {
      toast("대화를 불러오지 못했습니다.", "error");
    }
  };

  const exportConversation = () => {
    const lines = messages
      .filter((m) => m.text && !m.streaming)
      .map((m) => (m.role === "user" ? "■ 질문\n" : "▷ 답변\n") + m.text);
    if (lines.length === 0) return;
    const blob = new Blob([lines.join("\n\n────────\n\n")], {
      type: "text/plain;charset=utf-8",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `ArkiveAI_대화_${new Date().toISOString().slice(0, 10)}.txt`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const shareTurns = messages
    .filter((m) => m.text && !m.streaming && !m.error)
    .map((m) => ({ role: m.role, text: m.text }));
  const shareTitle = (messages.find((m) => m.role === "user")?.text ?? "ArkiveAI 공유 대화").slice(0, 60);

  const empty = messages.length === 0;
  const iconBtn =
    "flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-muted-foreground active:bg-muted";

  return (
    <div className="flex h-full flex-col">
      <HistoryDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        currentThreadId={threadId}
        onSelect={handleSelect}
        onNew={newThread}
        onDeletedCurrent={newThread}
      />
      <BookmarksDrawer open={bookmarksOpen} onClose={() => setBookmarksOpen(false)} />
      <ShareDialog open={shareOpen} onClose={() => setShareOpen(false)} title={shareTitle} turns={shareTurns} />
      <SettingsPanel open={settingsOpen} onClose={() => setSettingsOpen(false)} />
      <SchedulePanel
        open={scheduleOpen}
        initialItem={scheduleFocus}
        onChanged={upcoming.reload}
        onClose={() => {
          setScheduleOpen(false);
          setScheduleFocus(null);
        }}
      />
      <DictationPanel open={dictationOpen} onClose={() => setDictationOpen(false)} />
      <CustomerChangeDialog
        open={pendingCustomer !== null}
        targetLabel={pendingCustomer || "분야 전체"}
        onNewChat={changeWithNewChat}
        onKeepChat={changeKeepChat}
        onCancel={() => setPendingCustomer(null)}
      />
      <SessionGuard />
      <AnnouncementBanner />

      <header className="sticky top-0 z-10 flex items-center gap-1 border-b border-border bg-background/90 px-2 py-2 backdrop-blur pt-[env(safe-area-inset-top)]">
        <button onClick={() => setDrawerOpen(true)} className={iconBtn} aria-label="대화 이력" title="대화 이력">
          <Menu className="h-5 w-5" />
        </button>
        <div className="flex items-center gap-1.5 font-bold">
          <img src="/icon-512.png" alt="ArkiveAI" className="h-6 w-6 rounded-md" />
          <span className="text-[#0E1E2D]">ArkiveAI</span>
        </div>
        <div className="ml-1 flex-1">
          <CustomerSelect value={customer} onChange={onCustomer} />
        </div>
        {/* 일정 — 아이콘만 두면 있는 줄도 모르므로 라벨을 함께 노출하고,
            오늘 남은 일정 건수를 배지로 띄운다(대화 중에도 계속 보인다). */}
        <button
          onClick={() => openSchedule()}
          className="flex h-9 shrink-0 items-center gap-1 rounded-lg px-1.5 text-muted-foreground active:bg-muted"
          aria-label={
            upcoming.todayCount > 0 ? `일정 — 오늘 ${upcoming.todayCount}건` : "일정"
          }
          title="일정 보기/등록 (Zoom 사용 조율)"
        >
          <CalendarDays className="h-5 w-5" />
          <span className="hidden text-sm font-semibold md:inline">일정</span>
          {/* 건수는 겹치는 오버레이 배지 대신 나란한 알약으로 — 라벨과 부딪히지 않는다 */}
          {upcoming.todayCount > 0 && (
            <span className="min-w-[1.125rem] rounded-full bg-[#0E1E2D] px-1 text-center text-[10px] font-bold leading-[1.125rem] text-white">
              {upcoming.todayCount}
            </span>
          )}
        </button>
        <button
          onClick={() => setDictationOpen(true)}
          className={iconBtn}
          aria-label="받아쓰기"
          title="받아쓰기 — 음성 파일을 텍스트로(타임스탬프·화자분리)"
        >
          <Mic className="h-5 w-5" />
        </button>
        <button
          onClick={() => setShareOpen(true)}
          disabled={empty}
          className={iconBtn + " disabled:opacity-30"}
          aria-label="대화 공유 링크"
          title="읽기전용 공유 링크 만들기"
        >
          <Share2 className="h-5 w-5" />
        </button>
        <button
          onClick={() => setBookmarksOpen(true)}
          className={iconBtn}
          aria-label="즐겨찾기"
          title="즐겨찾기"
        >
          <Star className="h-5 w-5" />
        </button>
        <button
          onClick={exportConversation}
          disabled={empty}
          className={iconBtn + " disabled:opacity-30"}
          aria-label="대화 내보내기"
          title="대화 내보내기(txt)"
        >
          <Download className="h-5 w-5" />
        </button>
        <button onClick={() => { interactedRef.current = true; newThread(); }} className={iconBtn} aria-label="새 대화" title="새 대화">
          <Plus className="h-5 w-5" />
        </button>
        <button onClick={() => setSettingsOpen(true)} className={iconBtn} aria-label="설정" title="설정(글자 크기·고대비·알림)">
          <SettingsIcon className="h-5 w-5" />
        </button>
        <button onClick={onLogout} className={iconBtn} aria-label="로그아웃" title={user?.email ?? "로그아웃"}>
          <LogOut className="h-5 w-5" />
        </button>
      </header>

      <main className="flex-1 overflow-y-auto">
        {empty && restoring ? (
          // 이전 대화 복원 확인 중 — 홈을 먼저 그리면 복원 완료 순간
          // "첫 화면 → 이전 대화" 점프가 보이므로 짧은 로딩 상태로 대체한다.
          <div className="flex h-full items-center justify-center text-muted-foreground">
            <Loader2 className="h-5 w-5 animate-spin" />
          </div>
        ) : empty ? (
          // min-h-full: 내용이 짧으면 가운데, 길면 늘어나 스크롤된다.
          // (h-full 이면 일정·자료 위젯이 길어질 때 위쪽이 잘리고 스크롤도 되지 않았다)
          <div className="mx-auto flex min-h-full max-w-3xl flex-col items-center justify-center gap-5 px-6 py-6 text-center">
            <img src="/icon-512.png" alt="ArkiveAI" className="h-16 w-16 rounded-2xl" />
            <div>
              <h2 className="text-lg font-bold">무엇을 도와드릴까요?</h2>
              <p className="mt-1 text-sm text-muted-foreground">
                {customer ? `'${customer}' 기준으로 답변합니다.` : "분야를 선택하면 더 정확해요."}
              </p>
            </div>
            <HomeWidgets />
            <ul className="w-full max-w-md space-y-1.5 text-left text-sm text-muted-foreground">
              <li>· <b className="text-foreground">일정</b>에서 모임·회의를 확인하고, 상단 <b className="text-foreground">일정 📅</b>에서 등록·수정할 수 있어요.</li>
              <li>· 상단에서 <b className="text-foreground">분야</b>를 선택하고 질문을 하면 분야별 자료만 참고해서 답변을 합니다.</li>
              <li>· 답변의 <b className="text-foreground">출처</b>를 눌러 원문을 확인하고, 👍/👎·<b className="text-foreground">즐겨찾기 ⭐</b>로 표시해 두세요.</li>
              <li>· 상단 <b className="text-foreground">받아쓰기 🎙</b>에 음성 파일을 올리면 타임스탬프·화자를 구분해 텍스트로 바꿔 드려요.</li>
              <li>· 상단 <b className="text-foreground">공유</b>로 읽기전용 링크를 만들어 동료에게 전달할 수 있어요.</li>
              <li>· <b className="text-foreground">설정 ⚙</b>에서 답변 분량·말투·영어 답변을 조절할 수 있어요.</li>
              <li>· 왼쪽 위 <b className="text-foreground">☰</b>에서 지난 대화를 다시 열 수 있어요.</li>
            </ul>

            {/* 다가오는 일정 — 안내 문구 아래(첫 화면 맨 끝)에 둔다 */}
            <div className="mx-auto w-full max-w-2xl text-left">
              <UpcomingSchedules
                items={upcoming.items}
                loading={upcoming.loading}
                onOpen={openSchedule}
              />
            </div>
          </div>
        ) : (
          <MessageList
            messages={messages}
            customer={customer}
            onConfirm={confirmExternal}
            onFeedback={setFeedback}
            onEdit={(t) => setSeed({ text: t, nonce: Date.now() })}
            onRegenerate={(q) => send(q, customer, modelId)}
            onRegenerateSources={(q, urls, cust) => regenerateWithSources(q, cust ?? customer, urls)}
            onSuggest={(q) => send(q, customer, modelId)}
            onBookmark={setBookmark}
          />
        )}
      </main>

      <Composer
        busy={busy}
        onSend={(t) => {
          interactedRef.current = true; // 늦은 자동복원이 진행 중 대화를 덮지 않도록
          send(t, customer, modelId); // 선택 모델 유지 — 전송 후에도 복귀하지 않음
        }}
        onStop={stop}
        onSkillSend={(files, t, hint) => {
          interactedRef.current = true;
          runSkill(files, customer, t, hint, modelId); // 첨부 전송도 선택 모델 유지
        }}
        seed={seed}
        models={models}
        modelId={modelId}
        onModelChange={changeModel}
      />
      <Toasts />
    </div>
  );
}
